import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { AddressList } from "./delivery/AddressList";
import { AddressForm } from "./delivery/AddressForm";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DeliveryAddress } from "@/services/settings/settingsService";
import { useSettings } from "@/hooks/useSettings";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";
import { settingsLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";

export const DeliverySection = () => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<string | null>(null);
  const [editAddressId, setEditAddressId] = useState<string | null>(null);
  const labels = settingsLabels(getLanguage());
  const {
    deliveryAddresses,
    isLoadingDeliveryAddresses,
    setDefaultDeliveryAddressAsync,
    deleteDeliveryAddressAsync,
    updateDeliveryAddressAsync,
    createDeliveryAddressAsync,
  } = useSettings();

  const addresses = deliveryAddresses || [];
  const isLoading = isLoadingDeliveryAddresses;

  const handleSetDefault = async (addressId: string) => {
    try {
      await setDefaultDeliveryAddressAsync(addressId);
      toast.success(labels.delivery.defaultUpdated);
    } catch (error) {
      toast.error(labels.delivery.errorUpdating);
    }
  };

  const handleDeleteAddress = (addressId: string) => {
    if (addresses.length <= 1) {
      toast.error(
        labels.delivery.cannotDeleteLast ||
          "Du måste ha minst en leveransadress",
      );
      return;
    }
    setAddressToDelete(addressId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteAddress = async () => {
    if (addressToDelete !== null) {
      try {
        await deleteDeliveryAddressAsync(addressToDelete);
        toast.success(labels.delivery.addressDeleted);
        setShowDeleteDialog(false);
        setAddressToDelete(null);
      } catch (error) {
        toast.error(labels.delivery.errorDeleting);
      }
    }
  };

  const handleEditAddress = (addressId: string) => {
    setEditAddressId(addressId);
  };

  const handleUpdateAddress = async (address: {
    street: string;
    postalCode: string;
    city: string;
    country?: string;
    isDefault?: boolean;
  }) => {
    if (editAddressId) {
      try {
        await updateDeliveryAddressAsync({ id: editAddressId, data: address });
        toast.success(labels.delivery.addressUpdated || "Adress uppdaterad");
        setEditAddressId(null);
      } catch (error) {
        toast.error(labels.delivery.errorUpdating);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditAddressId(null);
  };

  const handleAddAddress = async (address: {
    street: string;
    postalCode: string;
    city: string;
    country?: string;
    isDefault?: boolean;
  }) => {
    try {
      await createDeliveryAddressAsync(address);
      toast.success(labels.delivery.addressAdded);
    } catch (error) {
      toast.error(labels.delivery.errorAdding);
    }
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <CardTitle className={`text-lg`}>{labels.delivery.title}</CardTitle>
        <CardDescription className={"text-primary/60"}>
          {user?.type === UserType.Dealer || user?.type === UserType.Business
            ? labels.delivery.descriptionBusiness
            : labels.delivery.description}
        </CardDescription>
        {(user?.type === UserType.Dealer ||
          user?.type === UserType.Business) && (
          <div className="mt-2 p-2 rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {labels.delivery.defaultInfo}
            </p>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {isLoading ? (
            <p>{labels.delivery.loading}</p>
          ) : (
            <>
              <AddressList
                addresses={addresses}
                onSetDefault={handleSetDefault}
                onDelete={handleDeleteAddress}
                onEdit={handleEditAddress}
              />

              <AddressForm
                onAddressAdded={handleAddAddress}
                onAddressUpdated={handleUpdateAddress}
                editAddressId={editAddressId}
                addresses={addresses}
                onCancelEdit={handleCancelEdit}
              />
            </>
          )}
        </div>
      </CardContent>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {labels.delivery.deleteAddress || "Radera adress"}
            </DialogTitle>
          </DialogHeader>
          <p>
            {labels.delivery.deleteAddressConfirm ||
              "Är du säker på att du vill radera denna adress?"}
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              {labels.delivery.cancel || "Avbryt"}
            </Button>
            <Button variant="destructive" onClick={confirmDeleteAddress}>
              {labels.delivery.delete || "Radera"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
